import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import LoginChecked from "../Shared/LoginChecked";
import {
  Card,
  Row,
  Col,
  Input,
  Form,
  Spin,
  Button,
  DatePicker,
  message,
  Typography,
  Alert,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { NavLink, useSearchParams } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";

const { Text, Title } = Typography;

function DipSettings() {
  const [searchParams] = useSearchParams();
  const tankIdParam = searchParams.get("tank_id");
  const selectedTankId = tankIdParam ? parseInt(tankIdParam, 10) : 0;
  const singleTankMode = selectedTankId > 0;

  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [dipsData, setDipsData] = useState([]);
  const [tankQuantities, setTankQuantities] = useState({});
  const [adjustmentRates, setAdjustmentRates] = useState({});
  const [Dated, setDated] = useState(dayjs().format("YYYY-MM-DD"));
  const [reloadKey, setReloadKey] = useState(0);
  const [focusedTankLabel, setFocusedTankLabel] = useState("");

  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");

  useEffect(() => {
    document.title = singleTankMode ? "Add DIP (Selected Tank)" : "Add Dip";
    setLoading(true);
    const data = {
      UserID: UserID,
      AccessKey: AccessKey,
      Dated: Dated,
    };

    const api_config = {
      method: "post",
      url: config.base_url + "Product/GetProductTanks",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(api_config)
      .then((response) => {
        if (response.data.status_code === 1) {
          let products = response.data.ListofProducts || [];

          if (singleTankMode) {
            const matched = products
              .map((product) => {
                const hasTank = (product.ListofTanks || []).some(
                  (t) => Number(t.ID) === selectedTankId
                );
                if (!hasTank) return null;
                // Keep all tanks of this product so physical vs book stock stays correct.
                return product;
              })
              .filter(Boolean);

            products = matched;
            if (matched.length > 0) {
              const focused = (matched[0].ListofTanks || []).find(
                (t) => Number(t.ID) === selectedTankId
              );
              setFocusedTankLabel(
                focused
                  ? `Tank ${focused.TankNo} — ${matched[0].Name || "Product"}`
                  : matched[0].Name || "Product"
              );
            } else {
              setFocusedTankLabel("");
              message.warning(
                "Selected tank was not found. Showing empty DIP form."
              );
            }
          } else {
            setFocusedTankLabel("");
          }

          setDipsData(products);

          const initialRates = products.reduce((acc, product) => {
            acc[product.ID] = product.Last_Purchase_Price;
            return acc;
          }, {});
          setAdjustmentRates(initialRates);

          const initialQuantities = products.reduce((acc, product) => {
            acc[product.ID] = (product.ListofTanks || []).reduce(
              (tankAcc, tank) => {
                tankAcc[tank.ID] = { stock: 0, ReadingValue: 0 };
                return tankAcc;
              },
              {}
            );
            return acc;
          }, {});
          setTankQuantities(initialQuantities);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [Dated, reloadKey, selectedTankId, singleTankMode]);

  const handleStockChange = (productId, tankId, field, value) => {
    setTankQuantities((prevQuantities) => ({
      ...prevQuantities,
      [productId]: {
        ...prevQuantities[productId],
        [tankId]: {
          ...prevQuantities[productId][tankId],
          [field]: Number(value) || 0,
        },
      },
    }));
  };

  const handleAdjustmentRateChange = (productId, value) => {
    setAdjustmentRates((prevRates) => ({
      ...prevRates,
      [productId]: value,
    }));
  };

  const handelSaveDip = () => {
    if (!Dated) {
      message.error("Please Select Date");
      return;
    }

    if (!dipsData.length) {
      message.error("No tank data to post.");
      return;
    }

    const listOfProductDIP = dipsData.map((product) => ({
      ProductID: product.ID,
      AdjustmentRate:
        parseFloat(adjustmentRates[product.ID]) ||
        parseFloat(product.Last_Purchase_Price) ||
        0,
      ListofDipReading: product.ListofTanks.map((tank) => ({
        Reading: tankQuantities[product.ID]?.[tank.ID]?.ReadingValue || 0,
        StockInLtr: tankQuantities[product.ID]?.[tank.ID]?.stock || 0,
        TankID: tank.ID,
      })),
    }));

    const data = {
      UserID: UserID,
      AccessKey: AccessKey,
      Date: Dated,
      ListofProductDIP: listOfProductDIP,
    };

    const api_config = {
      method: "post",
      url: config.base_url + "Product/AdjustDIP",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    setLoadingSave(true);
    axios(api_config)
      .then((response) => {
        if (response.data.status_code === 1) {
          message.success(response.data.status_message);
          setReloadKey((k) => k + 1);
        } else {
          message.error(response.data.status_message);
        }
        setLoadingSave(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingSave(false);
        message.error("Could not save DIP.");
      });
  };

  const calculateProductTotalQuantity = (productId) => {
    const productQuantities = tankQuantities[productId];
    if (!productQuantities) return 0;

    return Object.values(productQuantities).reduce(
      (total, tank) => total + (tank.stock || 0),
      0
    );
  };

  const disabledDate = (current) => {
    return current && current.isAfter(dayjs(), "day");
  };

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h6 className="page-title">
              {singleTankMode ? (
                <NavLink to="/dipsettings/manage-tanks">
                  <ChevronLeftIcon />
                </NavLink>
              ) : null}
              {singleTankMode
                ? `Add DIP${focusedTankLabel ? ` — ${focusedTankLabel}` : ""}`
                : "Add DIP"}
            </h6>
            {singleTankMode ? (
              <div className="header-actions">
                <NavLink to="/dipsettings">
                  <Button shape="round">View all tanks</Button>
                </NavLink>
              </div>
            ) : null}
          </div>
          <Card style={{ marginBottom: "16px" }}>
            <Row gutter={[24, 16]} align="middle">
              <Col lg={8} md={10} xs={24}>
                <Form layout="vertical">
                  <Form.Item
                    label="Posting date"
                    style={{ marginBottom: 0 }}
                    required
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      value={Dated ? dayjs(Dated) : null}
                      onChange={(date, dateString) =>
                        setDated(date ? dateString : "")
                      }
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                </Form>
              </Col>
              <Col lg={16} md={14} xs={24}>
                <Alert
                  type="info"
                  showIcon
                  message={
                    singleTankMode
                      ? "DIP for selected tank"
                      : "How to post DIP"
                  }
                  description={
                    singleTankMode
                      ? "Opened from Manage Tanks for one product. The selected tank is highlighted; other tanks of the same product are shown so stock adjustment stays accurate. DIP can be posted only once per date — remove from DIP Summary to re-post. Use “View all tanks” to post every product together."
                      : "Enter dip reading and actual stock for each tank. Book/system quantity is checked at save time (it is not tank capacity). You can post only once per date; delete from DIP Summary to re-post."
                  }
                />
              </Col>
            </Row>
          </Card>
          {loading ? (
            <div
              style={{
                height: "100vh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spin />
            </div>
          ) : (
            <Form layout="vertical">
              {!dipsData.length ? (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    singleTankMode
                      ? "Selected tank not found"
                      : "No products / tanks found"
                  }
                  description={
                    singleTankMode
                      ? "Go back to Manage Tanks and open Add DIP again, or use View all tanks."
                      : "Add tanks from Manage Tanks before posting DIP."
                  }
                  style={{ marginBottom: 16 }}
                />
              ) : null}
              {dipsData.map((product) => (
                <Card key={product.ID} style={{ marginBottom: "20px" }}>
                  <Row gutter={[16, 12]} align="middle">
                    <Col md={10} xs={24}>
                      <Title level={5} style={{ margin: 0 }}>
                        {product.Name}
                      </Title>
                      <Text type="secondary">
                        Tanks: {product.ListofTanks.length}
                        {singleTankMode ? " (this product only)" : ""}
                      </Text>
                    </Col>
                    <Col md={7} xs={12}>
                      <Text type="secondary">Book / system quantity (L)</Text>
                      <Input value={product.Balance} readOnly />
                    </Col>
                    <Col md={7} xs={12}>
                      <Text type="secondary">Adjustment rate (Rs./L)</Text>
                      <Input
                        value={adjustmentRates[product.ID] ?? ""}
                        placeholder="e.g. 285.50"
                        inputMode="decimal"
                        onChange={(e) =>
                          handleAdjustmentRateChange(product.ID, e.target.value)
                        }
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: "14px 0" }} />

                  <Row className="dip-table-header">
                    <Col md={8} xs={8}>
                      <Text strong>Tank No.</Text>
                    </Col>
                    <Col md={8} xs={8}>
                      <Text strong>Dip reading</Text>
                    </Col>
                    <Col md={8} xs={8}>
                      <Text strong>Physical stock (L)</Text>
                    </Col>
                  </Row>

                  {product.ListofTanks.length > 0 ? (
                    product.ListofTanks.map((tank) => {
                      const isFocused =
                        singleTankMode && Number(tank.ID) === selectedTankId;
                      return (
                        <Row
                          key={tank.ID}
                          className={`dip-data-row${isFocused ? " is-focused" : ""}`}
                          gutter={[8, 8]}
                          align="middle"
                        >
                          <Col md={8} xs={8}>
                            <Text strong={isFocused}>{tank.TankNo}</Text>
                          </Col>
                          <Col md={8} xs={8}>
                            <Input
                              value={
                                tankQuantities[product.ID]?.[tank.ID]
                                  ?.ReadingValue ?? ""
                              }
                              placeholder="Reading"
                              inputMode="decimal"
                              onChange={(e) =>
                                handleStockChange(
                                  product.ID,
                                  tank.ID,
                                  "ReadingValue",
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col md={8} xs={8}>
                            <Input
                              value={
                                tankQuantities[product.ID]?.[tank.ID]?.stock ??
                                ""
                              }
                              placeholder="Stock in litres"
                              inputMode="decimal"
                              onChange={(e) =>
                                handleStockChange(
                                  product.ID,
                                  tank.ID,
                                  "stock",
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                        </Row>
                      );
                    })
                  ) : (
                    <Row className="data-row">
                      <Col span={24}>
                        <Alert
                          type="warning"
                          showIcon
                          message="No tanks found for this product"
                          description="Add tanks from Manage Tanks before posting DIP."
                        />
                      </Col>
                    </Row>
                  )}

                  <Row
                    justify="end"
                    align="middle"
                    className="dip-table-footer"
                  >
                    <Col md={8} xs={12}>
                      <Text strong>Total physical (L)</Text>
                    </Col>
                    <Col md={6} xs={12}>
                      <Input
                        value={calculateProductTotalQuantity(product.ID)}
                        readOnly
                      />
                    </Col>
                  </Row>
                </Card>
              ))}

              {dipsData.length > 0 ? (
                <Row justify="end">
                  <Col md={8} xs={24}>
                    <Button
                      type="primary"
                      size="large"
                      shape="round"
                      block
                      style={{ marginBottom: "50px" }}
                      onClick={handelSaveDip}
                      disabled={!Dated || loadingSave}
                    >
                      Save Dip Adjustment
                      {loadingSave ? <Spin size="small" /> : ""}
                    </Button>
                  </Col>
                </Row>
              ) : null}
            </Form>
          )}
        </div>
      </div>
    </>
  );
}

export default DipSettings;
